import { useCallback } from 'react';
import { saveCampaign } from '../api/CampaignsAssociation';
import { useCampaignsContext } from '../CampaignsProvider';
import { SENTRY_KEYS } from 'campaigns-lib/constants/sentry';
import Raven from 'raven-js';
import { useSetAssociationsMutation } from 'campaigns-lib/__generated__/chirp/com/hubspot/campaigns/association/rpc/CampaignsAssociationRpc.hooks';
import { StandaloneUsageTracker } from 'campaigns-lib/utils/usageTracker';
export function useHandleCampaignChange({
  objectType,
  onChange = () => {},
  save = true,
  isOneToMany,
  selectedCampaignGuids,
  selectedAssetCrmId,
  setSelectedCampaignGuids,
  onSuccess = () => {},
  onFailure = () => {}
}) {
  const {
    setUpdatedCampaigns,
    setHasPendingChanges
  } = useCampaignsContext();
  const [setAssociations] = useSetAssociationsMutation();
  return useCallback(({
    target: {
      value: updatedCampaignGuids
    }
  }, selectOptions, selectedOption) => {
    const updatedCampaignGuidsArray = Array.isArray(updatedCampaignGuids) ? updatedCampaignGuids : [updatedCampaignGuids];
    const lastSelectedCampaign = updatedCampaignGuidsArray.length > 0 ? updatedCampaignGuidsArray[updatedCampaignGuidsArray.length - 1] : undefined;
    if (save && selectedAssetCrmId) {
      if (isOneToMany) {
        setAssociations({
          variables: {
            assetObjectTypeId: objectType,
            crmAssetIds: [Number(selectedAssetCrmId)],
            campaignGuids: updatedCampaignGuidsArray.filter(Boolean),
            primaryCampaignGuid: lastSelectedCampaign
          }
        }).then(() => {
          StandaloneUsageTracker.track('Campaigns Lib', 'Campaign Asset Association', {
            action: 'save campaign association',
            assetType: objectType,
            screen: 'campaign-select'
          });
          onSuccess({
            updatedCampaignGuids: updatedCampaignGuidsArray,
            selectedOption
          });
        }).catch(error => {
          onFailure();
          Raven.captureException(error, {
            fingerprint: [SENTRY_KEYS.CAMPAIGNS_PLATFORM_UPDATE_FAILED],
            tags: {
              lib: 'campaigns-lib'
            }
          });
        });
      } else {
        saveCampaign({
          objectType,
          selectedAssetCrmId,
          currentCampaignGuids: selectedCampaignGuids,
          updatedCampaignGuids: updatedCampaignGuidsArray,
          onSuccess: () => {
            StandaloneUsageTracker.track('Campaigns Lib', 'Campaign Asset Association', {
              action: 'save campaign association',
              assetType: objectType,
              screen: 'campaign-select'
            });
            onSuccess({
              updatedCampaignGuids: updatedCampaignGuidsArray,
              selectedOption
            });
          },
          onFailure
        }).catch(error => {
          Raven.captureException(error, {
            fingerprint: [SENTRY_KEYS.CAMPAIGNS_PLATFORM_UPDATE_FAILED],
            tags: {
              lib: 'campaigns-lib'
            }
          });
        });
      }
    }

    // Save the updated campaign to the context to be saved outside of the select.
    setUpdatedCampaigns(selectOptions.map(({
      guid,
      objectId
    }) => ({
      campaignGuid: guid,
      objectId: objectId
    })));
    setHasPendingChanges(true);

    // Update the selected campaign guid in the select.
    setSelectedCampaignGuids(updatedCampaignGuidsArray);

    // Invoke the select callback prop for consumers to use.
    onChange === null || onChange === void 0 || onChange(updatedCampaignGuidsArray, selectOptions);
  }, [isOneToMany, objectType, onChange, onFailure, onSuccess, save, selectedAssetCrmId, selectedCampaignGuids, setAssociations, setHasPendingChanges, setSelectedCampaignGuids, setUpdatedCampaigns]);
}
export default useHandleCampaignChange;